import * as React from "react";

import { compact, flatten } from "lodash";

import DocAddNode from "./BlockNodeHelpers/AddNode";

import DocAudio from "./BlockNodes/Audio";
import DocButton from "./BlockNodes/Button";
import DocCategoryMatcher from "./BlockNodes/CategoryMatcher";
import DocCDQQuestion from "./BlockNodes/CDQQuestion";
import DocCheckbox from "./BlockNodes/Checkbox";
import DocHorizontalRule from "./BlockNodes/HorizontalRule";
import DocImage from "./BlockNodes/Image";
import DocImageGallery from "./BlockNodes/ImageGallery";
import DocLearningObjective from "./BlockNodes/LearningObjective"
import DocLearningObjectivesList from "./BlockNodes/LearningObjectivesList";
import DocList from "./BlockNodes/List";
import DocMultipleChoice from "./BlockNodes/MultipleChoice";
import DocParagraph from "./BlockNodes/Paragraph";
import DocPdf from "./BlockNodes/Pdf";
import DocRatingBar from "./BlockNodes/RatingBar";
import DocSection from "./BlockNodes/Section";
import DocTable from "./BlockNodes/Table";
import DocTextInput from "./BlockNodes/TextInput";
import DocVariantPlaceholder from "./BlockNodes/VariantPlaceholder";
import DocVideo from "./BlockNodes/Video";
import AiAssistantChat from "./BlockNodes/AiAssistantChat";
import AiAssistantFeedback from "./BlockNodes/AiAssistantFeedback";
import LabValuesTable from "./BlockNodes/LabValuesTable";
import PatientInformation from "./BlockNodes/PatientInformation";
import AiAssistantTextInput from "./BlockNodes/AiAssistantTextInput";

export type DocBlockNodesProps = (
    DocSectionType<InlineNode> |
    DocTableCellType<InlineNode> |
    DocListItemNorrowedType<InlineNode> |
    DocLearningObjectivesListType<InlineNode> |
    DocTextInputType
  ) & {
  blockNodesContainsSelection?: boolean;
  cdqFeatures?: Array<CdqFeature>;
  content: Array<BlockNodeSectionChild<InlineNode>>;
  contentKey: string;
  currentMenu?: DocMenu;
  currentVersion: CurrentVersion;
  disableStyleTextEditing?: boolean;
  displayContext: DisplayContext;
  documentContext: DocumentContext;
  editingContext: EditingContext;
  featureFlag: FeatureFlag;
  inactiveBool?: boolean;
  independentlyEditable?: boolean;
  integrationInfo?: IntegrationInfo;
  level?: number;
  localClipboard?: LocalClipboardData;
  mediaContext: MediaContext;
  openSiblingSectionUIDs?: Array<string>;
  shouldHaveAddNodes?: boolean;
  simple?: boolean;
  userContext: UserContext;
  aiFallbackCards?: boolean;
};

export default function DocBlockNodes(props: DocBlockNodesProps) {
  const {
    content,
    inactiveBool,
    documentContext,
    currentVersion,
    editingContext,
    userContext,
    displayContext,
    mediaContext,
    featureFlag,
    currentMenu,
    localClipboard,
    simple,
    cdqFeatures,
    level,
    openSiblingSectionUIDs,
    shouldHaveAddNodes,
    contentKey,
    disableStyleTextEditing,
    independentlyEditable,
    blockNodesContainsSelection,
    integrationInfo,
    aiFallbackCards,
  } = props;

  const nodeLookup = {
    audio: DocAudio,
    button: DocButton,
    categoryMatcher: DocCategoryMatcher,
    cdqQuestion: DocCDQQuestion,
    checkbox: DocCheckbox,
    horizontalRule: DocHorizontalRule,
    image: DocImage,
    imageGallery: DocImageGallery,
    labValuesTable: LabValuesTable,
    patientInformation: PatientInformation,
    learningObjective: DocLearningObjective,
    learningObjectivesList: DocLearningObjectivesList,
    multipleChoice: DocMultipleChoice,
    numberedList: DocList,
    paragraph: DocParagraph,
    pdf: DocPdf,
    ratingBar: DocRatingBar,
    regularList: DocList,
    section: DocSection,
    table: DocTable,
    textInput: DocTextInput,
    variantPlaceholder: DocVariantPlaceholder,
    video: DocVideo,
    aiAssistantChat: AiAssistantChat,
    aiAssistantFeedback: AiAssistantFeedback,
    aiAssistantTextInput: AiAssistantTextInput,
  } as const;

  let lastNodeType;

  function checkMultipleSelect(data) {
    const multipleSelectElement = data.find(item =>
      ["multipleChoice", "cdqQuestion", "textInput", "ratingBar", "categoryMatcher"].includes(item.type)
    );

    if (!multipleSelectElement) return false;

    switch (multipleSelectElement.type) {
      case "multipleChoice":
      case "cdqQuestion":
        return Array.isArray(multipleSelectElement.content) &&
               multipleSelectElement.content.some(option => option.value === true);

      case "textInput":
      case "categoryMatcher":
        return multipleSelectElement.value !== "";

      case "ratingBar":
        return multipleSelectElement.value > 0;

      default:
        return false;
    }
  }

  let filteredContent = content.filter((node) => node);
  if(!editingContext.isEditing) {
    filteredContent = filteredContent.filter((element) => {
      const aiFlag = element && element.aiDisplayFlag;
      if (aiFallbackCards) {
      // If aiFallbackCards is present, always filter out aiOn
        return aiFlag !== "aiOn";
      }
      if (aiFlag === undefined) return true;
      if (props.featureFlag.vpe) {
        // If vpe is true, filter out aiDisplayFlag === "aiOff"
        return aiFlag !== "aiOff";
      } else {
        // If vpe is false, filter out aiDisplayFlag === "aiOn"
        return aiFlag !== "aiOn";
      }
    });
    if (displayContext.isUngated) {
      filteredContent = filteredContent.filter(element => !element.hideEducators);
    }
  }

  let contentNodes = filteredContent.map((node, index) => {
    const newInactiveBool = node.hasOwnProperty("inactive") && (node.inactive !== 0) ? node.inactive === 2 : inactiveBool;

    const trueOrEmptyOptions = blockNodesContainsSelection || disableStyleTextEditing ?
      {disableStyleTextEditing: true} : {}

    let onlyIfSectionOptions;
    if (node.type === "section") {
      onlyIfSectionOptions = {
        level: level + 1,
        openSiblingSectionUIDs: openSiblingSectionUIDs,
        cdqFeatures: node.cdqFeatures || cdqFeatures,
        integrationInfo: integrationInfo,
      };
    }

    let addNode: React.JSX.Element;
    if (shouldHaveAddNodes) {
      addNode = (
        <DocAddNode
          key={`doc-add-${contentKey}-${index}`}
          editIndex={node.editIndex}
          siblingUid={node.uid}
          documentContext={documentContext}
          editingContext={editingContext}
          userContext={userContext}
          currentMenu={currentMenu}
          localClipboard={localClipboard}
          parentNode={props}
          parentContentKey={contentKey}
          shouldHaveSpace={
            !["paragraph", "regularList", "numberedList"].includes(node.type) &&
            !["paragraph", "regularList", "numberedList"].includes(lastNodeType)
          }
        />
      );
    }

    lastNodeType = node.type;

    const NodeTag = nodeLookup[node.type];
    if (NodeTag) {
      return ([
        addNode,
        <NodeTag
          {...node}
          key={`doc-node-${contentKey}-${index}-${node.uid}`}
          documentContext={documentContext}
          currentVersion={currentVersion}
          editingContext={editingContext}
          userContext={userContext}
          displayContext={displayContext}
          mediaContext={mediaContext}
          featureFlag={featureFlag}
          simple={simple}
          inactiveBool={(node.type=='button' && node.title=='Submit')? !checkMultipleSelect(content) : newInactiveBool}
          cdqFeatures={cdqFeatures}
          {...trueOrEmptyOptions}
          {...onlyIfSectionOptions}
        />,
      ]);
    } else {
      // Rollbar.warning(`DocBlockNodes does not recognize node of type ${node.type}`);
      return [];
    }
  });

  if (shouldHaveAddNodes) {
    contentNodes.push([
      <DocAddNode
        key={"--0"}
        documentContext={documentContext}
        editingContext={editingContext}
        userContext={userContext}
        currentMenu={currentMenu}
        localClipboard={localClipboard}
        parentNode={props}
        parentContentKey={contentKey}
        shouldHaveSpace={
          !["paragraph", "regularList", "numberedList"].includes(lastNodeType)
        }
      />
    ]);
  }


  if (editingContext.isEditing) {
    let editableData = {}
    if (independentlyEditable) {
      editableData = {
        "data-is-independently-editable": true,
        style: {
          "userSelect": "auto",
          "WebkitUserSelect": "auto",
        },
      };

      if (!disableStyleTextEditing) {
        editableData = {
          ...editableData,
          contentEditable: true
        }
      }
    }

    return (
      <div className="doc-children"
        data-is-block-nodes
        {...editableData}
        suppressContentEditableWarning
        autoCorrect={"off"}
        autoCapitalize={"off"}
        spellCheck={false}
        onDrop={(e) => {e.stopPropagation(); e.preventDefault();}}
      >
        {compact(flatten(contentNodes))}
      </div>
    );
  } else {
    return (
      <div className="doc-children">
        {compact(flatten(contentNodes))}
      </div>
    );
  }
}
